import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, UserPlus, BarChart3, Settings, Bell, ChevronLeft, ChevronRight, Plus, Edit, Trash2, CheckCircle, XCircle, AlertTriangle, DollarSign, TrendingUp, LogOut, RefreshCw, User, Megaphone, Send, Activity, Filter, Search } from 'lucide-react';
import { auth, db } from './firebase/config';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, collection, onSnapshot, query, where, addDoc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { seedDatabase } from './seed';

const positions = ['Server', 'Line Cook', 'Prep Cook', 'Bartender', 'Host/Hostess', 'FOH Manager', 'Kitchen Manager', 'Dishwasher'];

// The main application logic is encapsulated in this component.
function SchedulingApp() {
  const [currentUser, setCurrentUser] = useState(null);
  const [activeView, setActiveView] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [shiftSwapRequests, setShiftSwapRequests] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const docRef = doc(db, 'employees', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCurrentUser({ ...docSnap.data(), id: docSnap.id });
        } else {
          // Handle case where user exists in Auth but not in Firestore
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch collections from Firestore
  useEffect(() => {
    // Employees
    const employeeUnsubscribe = onSnapshot(collection(db, 'employees'), (snapshot) => {
      setEmployees(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    // Shifts
    const shiftUnsubscribe = onSnapshot(collection(db, 'shifts'), (snapshot) => {
      setShifts(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    // Time Off Requests
    const timeOffUnsubscribe = onSnapshot(collection(db, 'timeOffRequests'), (snapshot) => {
      setTimeOffRequests(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    // Shift Swap Requests
    const swapUnsubscribe = onSnapshot(collection(db, 'shiftSwapRequests'), (snapshot) => {
      setShiftSwapRequests(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    // Announcements
    const announcementUnsubscribe = onSnapshot(collection(db, 'announcements'), (snapshot) => {
      setAnnouncements(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    // Activity Log
    const activityUnsubscribe = onSnapshot(collection(db, 'activityLog'), (snapshot) => {
      setActivityLog(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    return () => {
      employeeUnsubscribe();
      shiftUnsubscribe();
      timeOffUnsubscribe();
      swapUnsubscribe();
      announcementUnsubscribe();
      activityUnsubscribe();
    };
  }, []);

  // Fetch notifications for the current user
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }
    const q = query(collection(db, 'notifications'), where('userId', '==', currentUser.id));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNotifications(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Helper function to log activities
  const logActivity = async (userId, action, details, relatedData = null) => {
    try {
      await addDoc(collection(db, 'activityLog'), {
        userId,
        action,
        details,
        timestamp: new Date().toISOString(),
        ipAddress: '192.168.1.' + Math.floor(Math.random() * 255), // Simulated IP
        relatedData,
      });
    } catch (error) {
      console.error("Error logging activity: ", error);
    }
  };

  // Helper function to create notifications
  const createNotification = async (userId, type, message, relatedId = null) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        message,
        timestamp: new Date().toISOString(),
        read: false,
        relatedId,
      });
    } catch (error) {
      console.error("Error creating notification: ", error);
    }
  };

  // Helper function to notify multiple users
  const notifyUsers = (userIds, type, message, relatedId = null) => {
    const batch = writeBatch(db);
    userIds.forEach(userId => {
      const notificationRef = doc(collection(db, 'notifications'));
      batch.set(notificationRef, {
        userId,
        type,
        message,
        timestamp: new Date().toISOString(),
        read: false,
        relatedId,
      });
    });
    batch.commit().catch(error => console.error("Error notifying users: ", error));
  };

  // Login Component
  const LoginForm = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
      e.preventDefault();
      setError('');
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        // onAuthStateChanged will handle setting the user
        logActivity(userCredential.user.uid, 'login', `User logged in`);
      } catch (error) {
        setError('Invalid email or password');
        console.error("Authentication error:", error);
      }
    };

    const demoLogin = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            logActivity(userCredential.user.uid, 'demo_login', `Demo user logged in`);
        } catch (error) {
            setError('Could not log in as demo user.');
            console.error("Demo login error:", error);
        }
    };

    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-indigo-800">SchedulePro</h1>
            <p className="text-gray-600">Employee Scheduling System</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <button
              type="submit"
              className="w-full bg-indigo-600 text-white p-3 rounded-lg hover:bg-indigo-700"
            >
              Login
            </button>
          </form>

          <div className="mt-6 border-t pt-6">
            <p className="text-center text-gray-600 mb-4">Demo Logins:</p>
            <div className="space-y-2">
              <button
                onClick={() => demoLogin('lisa@restaurant.com', 'admin123')}
                className="w-full bg-red-100 text-red-800 p-2 rounded text-sm hover:bg-red-200"
              >
                Admin (Full Access) - Send announcements to all
              </button>
              <button
                onClick={() => demoLogin('sarah@restaurant.com', 'manager123')}
                className="w-full bg-yellow-100 text-yellow-800 p-2 rounded text-sm hover:bg-yellow-200"
              >
                Manager (Schedule Management) - Send announcements to employees
              </button>
              <button
                onClick={() => demoLogin('mike@restaurant.com', 'employee123')}
                className="w-full bg-green-100 text-green-800 p-2 rounded text-sm hover:bg-green-200"
              >
                Employee (Self-Service) - Receive announcements only
              </button>
            </div>
          </div>

          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 border-t pt-6">
              <button
                onClick={seedDatabase}
                className="w-full bg-gray-200 text-gray-800 p-2 rounded text-sm hover:bg-gray-300"
              >
                Seed Database (Dev Only)
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Get week dates
  const getWeekDates = (date) => {
    const week = [];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const weekDates = getWeekDates(currentWeek);

  // Notifications Component
  const NotificationsDropdown = () => {
    const userNotifications = notifications.filter(n => n.userId === currentUser.id).slice(0, 10);
    const unreadCount = userNotifications.filter(n => !n.read).length;

    const markAsRead = async (notificationId) => {
      const notificationRef = doc(db, 'notifications', notificationId);
      try {
        await updateDoc(notificationRef, { read: true });
      } catch (error) {
        console.error("Error marking notification as read: ", error);
      }
    };

    const markAllAsRead = () => {
      const batch = writeBatch(db);
      notifications.forEach(notification => {
        if (!notification.read) {
          const notificationRef = doc(db, 'notifications', notification.id);
          batch.update(notificationRef, { read: true });
        }
      });
      batch.commit().catch(error => console.error("Error marking all notifications as read: ", error));
    };

    const getNotificationIcon = (type) => {
      switch (type) {
        case 'swap_request': return <RefreshCw className="w-4 h-4 text-blue-500" />;
        case 'swap_agreed': return <CheckCircle className="w-4 h-4 text-green-500" />;
        case 'swap_approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
        case 'swap_denied': return <XCircle className="w-4 h-4 text-red-500" />;
        case 'swap_approval_needed': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
        case 'time_off_approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
        case 'time_off_denied': return <XCircle className="w-4 h-4 text-red-500" />;
        case 'schedule_change': return <Calendar className="w-4 h-4 text-purple-500" />;
        case 'new_shift': return <Plus className="w-4 h-4 text-blue-500" />;
        case 'announcement': return <Megaphone className="w-4 h-4 text-orange-500" />;
        default: return <Bell className="w-4 h-4 text-gray-500" />;
      }
    };

    const formatTime = (timestamp) => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${diffDays}d ago`;
    };

    return (
      <div className="relative">
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="relative p-2 hover:bg-indigo-700 rounded"
        >
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>

        {showNotifications && (
          <div className="notifications-dropdown absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Mark all read
                  </button>
                )}
              </div>
            </div>
            
            <div className="max-h-80 overflow-y-auto">
              {userNotifications.length > 0 ? (
                userNotifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-3 border-b hover:bg-gray-50 cursor-pointer ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => !notification.read && markAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-medium' : ''}`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatTime(notification.timestamp)}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No notifications</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Navigation Component
  const Navigation = () => (
    <nav className="bg-indigo-800 text-white p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">SchedulePro</h1>
        <div className="flex items-center space-x-4">
          {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
            <>
              <button 
                onClick={() => setActiveView('dashboard')}
                className={`px-3 py-2 rounded ${activeView === 'dashboard' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
              >
                <BarChart3 className="w-4 h-4 inline mr-1" /> Dashboard
              </button>
              <button 
                onClick={() => setActiveView('employees')}
                className={`px-3 py-2 rounded ${activeView === 'employees' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
              >
                <Users className="w-4 h-4 inline mr-1" /> Employees
              </button>
            </>
          )}
          <button 
            onClick={() => setActiveView('schedule')}
            className={`px-3 py-2 rounded ${activeView === 'schedule' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
          >
            <Calendar className="w-4 h-4 inline mr-1" /> Schedule
          </button>
          <button 
            onClick={() => setActiveView('timeoff')}
            className={`px-3 py-2 rounded ${activeView === 'timeoff' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
          >
            <Clock className="w-4 h-4 inline mr-1" /> Time Off
          </button>
          {currentUser.role === 'employee' && (
            <button 
              onClick={() => setActiveView('swaps')}
              className={`px-3 py-2 rounded ${activeView === 'swaps' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
            >
              <RefreshCw className="w-4 h-4 inline mr-1" /> Swaps
            </button>
          )}
          <button 
            onClick={() => setActiveView('announcements')}
            className={`px-3 py-2 rounded ${activeView === 'announcements' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
          >
            <Megaphone className="w-4 h-4 inline mr-1" /> Announcements
          </button>
          {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
            <button 
              onClick={() => setActiveView('approvals')}
              className={`px-3 py-2 rounded ${activeView === 'approvals' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
            >
              <CheckCircle className="w-4 h-4 inline mr-1" /> Approvals
            </button>
          )}
          {currentUser.role === 'admin' && (
            <button 
              onClick={() => setActiveView('activity')}
              className={`px-3 py-2 rounded ${activeView === 'activity' ? 'bg-indigo-600' : 'hover:bg-indigo-700'}`}
            >
              <Activity className="w-4 h-4 inline mr-1" /> Activity
            </button>
          )}
          <div className="flex items-center space-x-2 border-l pl-4">
            <NotificationsDropdown />
            <User className="w-4 h-4" />
            <span className="text-sm">{currentUser.name}</span>
            <button
              onClick={async () => {
                await signOut(auth);
                // The onAuthStateChanged listener will handle cleanup.
              }}
              className="p-2 hover:bg-indigo-700 rounded"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );

  // Custom Time Picker Component
  const TimePicker = ({ value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tempTime, setTempTime] = useState(value || '09:00');
    
    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = ['00', '15', '30', '45'];
    const periods = ['AM', 'PM'];
    
    const formatTime = (time24) => {
      if (!time24) return '';
      const [hour, minute] = time24.split(':');
      const hour12 = hour > 12 ? hour - 12 : (hour === '0' ? 12 : parseInt(hour));
      const period = hour >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minute} ${period}`;
    };
    
    const convertTo24Hour = (hour12, minute, period) => {
      let hour24 = parseInt(hour12);
      if (period === 'AM' && hour24 === 12) hour24 = 0;
      if (period === 'PM' && hour24 !== 12) hour24 += 12;
      return `${hour24.toString().padStart(2, '0')}:${minute}`;
    };
    
    const parseCurrentTime = (time24) => {
      if (!time24) return { hour: 9, minute: '00', period: 'AM' };
      const [hour, minute] = time24.split(':');
      const hour12 = hour > 12 ? hour - 12 : (hour === '0' ? 12 : parseInt(hour));
      const period = hour >= 12 ? 'PM' : 'AM';
      return { hour: hour12, minute, period };
    };
    
    const currentTime = parseCurrentTime(tempTime);
    
    const handleTimeChange = (type, newValue) => {
      let newHour = currentTime.hour;
      let newMinute = currentTime.minute;
      let newPeriod = currentTime.period;
      
      if (type === 'hour') newHour = newValue;
      if (type === 'minute') newMinute = newValue;
      if (type === 'period') newPeriod = newValue;
      
      const time24 = convertTo24Hour(newHour, newMinute, newPeriod);
      setTempTime(time24);
    };
    
    const handleApply = () => {
      onChange(tempTime);
      setIsOpen(false);
    };
    
    return (
      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={formatTime(value)}
          onClick={() => setIsOpen(true)}
          readOnly
          className="w-full p-2 border rounded cursor-pointer hover:bg-gray-50"
        />
        
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 p-4">
            <div className="flex space-x-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2">Hour</label>
                <select
                  value={currentTime.hour}
                  onChange={(e) => handleTimeChange('hour', parseInt(e.target.value))}
                  className="p-2 border rounded"
                >
                  {hours.map(hour => (
                    <option key={hour} value={hour}>{hour}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Minute</label>
                <select
                  value={currentTime.minute}
                  onChange={(e) => handleTimeChange('minute', e.target.value)}
                  className="p-2 border rounded"
                >
                  {minutes.map(minute => (
                    <option key={minute} value={minute}>{minute}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">AM/PM</label>
                <select
                  value={currentTime.period}
                  onChange={(e) => handleTimeChange('period', e.target.value)}
                  className="p-2 border rounded"
                >
                  {periods.map(period => (
                    <option key={period} value={period}>{period}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setIsOpen(false)}
                className="px-3 py-1 text-sm text-gray-600 border rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Dashboard Component (Admin/Manager only)
  const Dashboard = () => {
    const totalEmployees = employees.filter(e => e.status === 'active').length;
    const todayShifts = shifts.filter(s => s.date === '2025-09-23').length;
    const pendingTimeOff = timeOffRequests.filter(r => r.status === 'pending').length;
    const pendingSwaps = shiftSwapRequests.filter(r => r.status === 'pending').length;
    const weeklyHours = shifts.reduce((total, shift) => {
      const start = new Date(`2000-01-01 ${shift.startTime}`);
      const end = new Date(`2000-01-01 ${shift.endTime}`);
      return total + (end - start) / (1000 * 60 * 60);
    }, 0);

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Dashboard</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Active Employees</p>
                <p className="text-2xl font-bold">{totalEmployees}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Today's Shifts</p>
                <p className="text-2xl font-bold">{todayShifts}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Bell className="w-8 h-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Pending Requests</p>
                <p className="text-2xl font-bold">{pendingTimeOff + pendingSwaps}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Weekly Hours</p>
                <p className="text-2xl font-bold">{weeklyHours.toFixed(1)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3" />
              <span>Mike Chen clocked in at 10:00 AM</span>
            </div>
            <div className="flex items-center text-sm">
              <AlertTriangle className="w-4 h-4 text-yellow-500 mr-3" />
              <span>Emma Davis requested time off for Sept 30-Oct 2</span>
            </div>
            <div className="flex items-center text-sm">
              <RefreshCw className="w-4 h-4 text-blue-500 mr-3" />
              <span>Emma Davis requested shift swap for Sept 25</span>
            </div>
            <div className="flex items-center text-sm">
              <Megaphone className="w-4 h-4 text-orange-500 mr-3" />
              <span>New uniform policy announcement sent to all staff</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Employee Dashboard
  const EmployeeDashboard = () => {
    const myShifts = shifts.filter(shift => shift.employeeId === currentUser.id);
    const upcomingShifts = myShifts.filter(shift => new Date(shift.date) >= new Date()).slice(0, 5);
    const myTimeOffRequests = timeOffRequests.filter(req => req.employeeId === currentUser.id);
    const mySwapRequests = shiftSwapRequests.filter(req => req.fromEmployeeId === currentUser.id);
    const unreadAnnouncements = announcements.filter(ann => 
      ann.targetRoles.includes(currentUser.role) && !ann.readBy.includes(currentUser.id)
    ).length;
    const thisWeekHours = myShifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      return shiftDate >= weekStart && shiftDate <= weekEnd;
    }).reduce((total, shift) => {
      const start = new Date(`2000-01-01 ${shift.startTime}`);
      const end = new Date(`2000-01-01 ${shift.endTime}`);
      return total + (end - start) / (1000 * 60 * 60);
    }, 0);

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">My Dashboard</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Calendar className="w-8 h-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">This Week Hours</p>
                <p className="text-2xl font-bold">{thisWeekHours.toFixed(1)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Clock className="w-8 h-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Time Off Requests</p>
                <p className="text-2xl font-bold">{myTimeOffRequests.length}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <RefreshCw className="w-8 h-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Swap Requests</p>
                <p className="text-2xl font-bold">{mySwapRequests.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Megaphone className="w-8 h-8 text-orange-500" />
              <div className="ml-4">
                <p className="text-sm text-gray-600">Unread Announcements</p>
                <p className="text-2xl font-bold">{unreadAnnouncements}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Upcoming Shifts</h3>
          <div className="space-y-3">
            {upcomingShifts.map(shift => (
              <div key={shift.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{new Date(shift.date).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600">{shift.position}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{shift.startTime} - {shift.endTime}</p>
                </div>
              </div>
            ))}
            {upcomingShifts.length === 0 && (
              <p className="text-gray-500">No upcoming shifts scheduled</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <button
            onClick={() => {
              setSelectedEmployee({ 
                employeeId: currentUser.id, 
                startDate: '', 
                endDate: '', 
                reason: '' 
              });
              setModalType('timeOffRequest');
              setIsModalOpen(true);
            }}
            className="bg-blue-100 text-blue-800 p-4 rounded-lg hover:bg-blue-200 text-left"
          >
            <Clock className="w-6 h-6 mb-2" />
            <h3 className="font-semibold">Request Time Off</h3>
            <p className="text-sm">Submit a new time off request</p>
          </button>
          
          <button
            onClick={() => setActiveView('swaps')}
            className="bg-purple-100 text-purple-800 p-4 rounded-lg hover:bg-purple-200 text-left"
          >
            <RefreshCw className="w-6 h-6 mb-2" />
            <h3 className="font-semibold">Swap Shifts</h3>
            <p className="text-sm">Request to swap shifts with colleagues</p>
          </button>

          <button
            onClick={() => setActiveView('announcements')}
            className="bg-orange-100 text-orange-800 p-4 rounded-lg hover:bg-orange-200 text-left"
          >
            <Megaphone className="w-6 h-6 mb-2" />
            <h3 className="font-semibold">View Announcements</h3>
            <p className="text-sm">Check latest company announcements</p>
          </button>
        </div>
      </div>
    );
  };

  // Schedule Component
  const Schedule = () => {
    const navigateWeek = (direction) => {
      const newWeek = new Date(currentWeek);
      newWeek.setDate(newWeek.getDate() + (direction * 7));
      setCurrentWeek(newWeek);
    };

    const getShiftsForDay = (date) => {
      const dateStr = date.toISOString().split('T')[0];
      let dayShifts = shifts.filter(shift => shift.date === dateStr);
      
      if (currentUser.role === 'employee') {
        dayShifts = dayShifts.filter(shift => shift.employeeId === currentUser.id);
      }
      
      return dayShifts.map(shift => ({
        ...shift,
        employee: employees.find(emp => emp.id === shift.employeeId)
      }));
    };

    const openShiftModal = (date = null, shiftId = null) => {
      if (currentUser.role === 'employee') return;
      
      setSelectedEmployee(shiftId ? shifts.find(s => s.id === shiftId) : { date: date?.toISOString().split('T')[0] });
      setModalType('shift');
      setIsModalOpen(true);
    };

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {currentUser.role === 'employee' ? 'My Schedule' : 'Weekly Schedule'}
          </h2>
          <div className="flex items-center space-x-4">
            <button 
              onClick={() => navigateWeek(-1)}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-medium">
              {weekDates[0].toLocaleDateString()} - {weekDates[6].toLocaleDateString()}
            </span>
            <button 
              onClick={() => navigateWeek(1)}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
            {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
              <button 
                onClick={() => openShiftModal()}
                className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 inline mr-1" /> Add Shift
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="grid grid-cols-8 gap-0">
            <div className="p-4 bg-gray-50 font-medium">Time</div>
            {weekDates.map((date, index) => (
              <div key={index} className="p-4 bg-gray-50 font-medium text-center">
                <div>{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}</div>
                <div className="text-sm text-gray-600">{date.getDate()}</div>
              </div>
            ))}
          </div>

          {Array.from({ length: 15 }, (_, hour) => hour + 8).map(hour => (
            <div key={hour} className="grid grid-cols-8 gap-0 border-t">
              <div className="p-2 bg-gray-50 text-sm">
                {hour === 24 ? '12:00 AM' : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`}
              </div>
              {weekDates.map((date, dayIndex) => {
                const dayShifts = getShiftsForDay(date);
                const hourShifts = dayShifts.filter(shift => {
                  const startHour = parseInt(shift.startTime.split(':')[0]);
                  const endHour = parseInt(shift.endTime.split(':')[0]);
                  return hour >= startHour && hour < endHour;
                });

                return (
                  <div 
                    key={dayIndex} 
                    className={`p-2 border-l min-h-[60px] relative ${
                      currentUser.role !== 'employee' ? 'hover:bg-gray-50 cursor-pointer' : ''
                    }`}
                    onClick={() => currentUser.role !== 'employee' && openShiftModal(date)}
                  >
                    {hourShifts.map(shift => (
                      <div 
                        key={shift.id}
                        className={`text-xs p-1 rounded mb-1 ${
                          currentUser.role === 'employee' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800 cursor-pointer'
                        }`}
                        onClick={(e) => {
                          if (currentUser.role !== 'employee') {
                            e.stopPropagation();
                            openShiftModal(null, shift.id);
                          }
                        }}
                      >
                        {currentUser.role === 'employee' ? (
                          <div>
                            <div className="font-medium">{shift.position}</div>
                            <div>{shift.startTime} - {shift.endTime}</div>
                          </div>
                        ) : (
                          <div>
                            <div className="font-medium">{shift.employee?.name}</div>
                            <div>{shift.position}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Employees Component (Admin/Manager only)
  const Employees = () => {
    const openEmployeeModal = (employee = null) => {
      setSelectedEmployee(employee || { name: '', role: 'employee', position: '', email: '', phone: '', hourlyRate: 0 });
      setModalType('employee');
      setIsModalOpen(true);
    };

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Employees</h2>
          <button 
            onClick={() => openEmployeeModal()}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            <UserPlus className="w-4 h-4 inline mr-1" /> Add Employee
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hourly Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {employees.map(employee => (
                <tr key={employee.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{employee.position}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      employee.role === 'admin' ? 'bg-red-100 text-red-800' :
                      employee.role === 'manager' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {employee.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${employee.hourlyRate}/hr</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{employee.email}</div>
                    <div>{employee.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button 
                      onClick={() => openEmployeeModal(employee)}
                      className="text-indigo-600 hover:text-indigo-900 mr-2"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-900">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Time Off Component
  const TimeOff = () => {
    const approveTimeOff = async (requestId) => {
      const requestRef = doc(db, 'timeOffRequests', requestId);
      try {
        await updateDoc(requestRef, { status: 'approved' });
        const request = timeOffRequests.find(req => req.id === requestId);
        createNotification(request.employeeId, 'time_off_approved',
          `Time off approved for ${request.startDate} to ${request.endDate}`, requestId);
        const employee = employees.find(emp => emp.id === request.employeeId);
        logActivity(currentUser.id, 'time_off_approved',
          `Approved time off request for ${employee?.name} (${request.startDate} to ${request.endDate})`,
          { requestId, employeeId: request.employeeId });
      } catch (error) {
        console.error("Error approving time off: ", error);
      }
    };

    const denyTimeOff = async (requestId) => {
      const requestRef = doc(db, 'timeOffRequests', requestId);
      try {
        await updateDoc(requestRef, { status: 'denied' });
        const request = timeOffRequests.find(req => req.id === requestId);
        createNotification(request.employeeId, 'time_off_denied',
          `Time off denied for ${request.startDate} to ${request.endDate}`, requestId);
        const employee = employees.find(emp => emp.id === request.employeeId);
        logActivity(currentUser.id, 'time_off_denied',
          `Denied time off request for ${employee?.name} (${request.startDate} to ${request.endDate})`,
          { requestId, employeeId: request.employeeId });
      } catch (error) {
        console.error("Error denying time off: ", error);
      }
    };

    const relevantRequests = currentUser.role === 'employee' 
      ? timeOffRequests.filter(req => req.employeeId === currentUser.id)
      : timeOffRequests;

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">
            {currentUser.role === 'employee' ? 'My Time Off Requests' : 'Time Off Requests'}
          </h2>
          <button 
            onClick={() => {
              setSelectedEmployee({ 
                employeeId: currentUser.id, 
                startDate: '', 
                endDate: '', 
                reason: '' 
              });
              setModalType('timeOffRequest');
              setIsModalOpen(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4 inline mr-1" /> New Request
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {currentUser.role !== 'employee' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {relevantRequests.map(request => {
                const employee = employees.find(emp => emp.id === request.employeeId);
                return (
                  <tr key={request.id}>
                    {currentUser.role !== 'employee' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {employee?.name}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {request.startDate} to {request.endDate}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{request.reason}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {request.status}
                      </span>
                    </td>
                    {(currentUser.role === 'admin' || currentUser.role === 'manager') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {request.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => approveTimeOff(request.id)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => denyTimeOff(request.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Activity Log Component (Admin only)
  const ActivityLog = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('all');

    const actionTypes = [
      'all', 'login', 'logout', 'demo_login', 'shift_created', 'shift_updated', 'shift_deleted',
      'employee_created', 'employee_updated', 'employee_deleted', 'time_off_requested', 
      'time_off_approved', 'time_off_denied', 'shift_swap_requested', 'shift_swap_approved', 
      'shift_swap_denied', 'announcement_sent', 'announcement_deleted'
    ];

    const getDateRange = (filter) => {
      const now = new Date();
      switch (filter) {
        case 'today':
          return { start: new Date(now.setHours(0, 0, 0, 0)), end: new Date(now.setHours(23, 59, 59, 999)) };
        case 'week':
          const weekStart = new Date(now);
          weekStart.setDate(now.getDate() - now.getDay());
          weekStart.setHours(0, 0, 0, 0);
          return { start: weekStart, end: now };
        case 'month':
          return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
        default:
          return null;
      }
    };

    const filteredActivities = activityLog.filter(activity => {
      const employee = employees.find(emp => emp.id === activity.userId);
      const activityDate = new Date(activity.timestamp);
      const dateRange = getDateRange(dateFilter);

      const matchesSearch = searchTerm === '' || 
        activity.details.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity.action.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesAction = actionFilter === 'all' || activity.action === actionFilter;
      const matchesDate = dateFilter === 'all' || 
        (dateRange && activityDate >= dateRange.start && activityDate <= dateRange.end);
      const matchesUser = userFilter === 'all' || activity.userId.toString() === userFilter;

      return matchesSearch && matchesAction && matchesDate && matchesUser;
    });

    const getActionIcon = (action) => {
      switch (action) {
        case 'login': case 'demo_login': return <LogOut className="w-4 h-4 text-green-500 rotate-180" />;
        case 'logout': return <LogOut className="w-4 h-4 text-red-500" />;
        case 'shift_created': case 'shift_updated': return <Calendar className="w-4 h-4 text-blue-500" />;
        case 'employee_created': case 'employee_updated': return <Users className="w-4 h-4 text-purple-500" />;
        case 'time_off_requested': case 'time_off_approved': case 'time_off_denied': return <Clock className="w-4 h-4 text-orange-500" />;
        case 'shift_swap_requested': case 'shift_swap_approved': case 'shift_swap_denied': return <RefreshCw className="w-4 h-4 text-indigo-500" />;
        case 'announcement_sent': case 'announcement_deleted': return <Megaphone className="w-4 h-4 text-yellow-500" />;
        default: return <Activity className="w-4 h-4 text-gray-500" />;
      }
    };

    const getActionColor = (action) => {
      if (action.includes('login')) return 'text-green-600 bg-green-50';
      if (action.includes('logout')) return 'text-red-600 bg-red-50';
      if (action.includes('created') || action.includes('approved')) return 'text-green-600 bg-green-50';
      if (action.includes('deleted') || action.includes('denied')) return 'text-red-600 bg-red-50';
      if (action.includes('updated') || action.includes('requested')) return 'text-blue-600 bg-blue-50';
      return 'text-gray-600 bg-gray-50';
    };

    const formatTimestamp = (timestamp) => {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 24) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `${Math.floor(diffHours / 24)}d ago`;
    };

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">System Activity Log</h2>
          <div className="text-sm text-gray-500">
            Total Activities: {filteredActivities.length} / {activityLog.length}
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search activities..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {actionTypes.map(action => (
                <option key={action} value={action}>
                  {action === 'all' ? 'All Actions' : action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Users</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.name} ({emp.role})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-3">
          {filteredActivities.map(activity => {
            const employee = employees.find(emp => emp.id === activity.userId);
            return (
              <div key={activity.id} className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 mt-1">{getActionIcon(activity.action)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900">{employee?.name || 'Unknown User'}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                          {activity.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{activity.details}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-400">
                        <span>{new Date(activity.timestamp).toLocaleString()}</span>
                        <span>IP: {activity.ipAddress}</span>
                        <span>ID: {activity.id}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 ml-4">{formatTimestamp(activity.timestamp)}</div>
                </div>
              </div>
            );
          })}
          {filteredActivities.length === 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Found</h3>
              <p className="text-gray-500">Try adjusting your filters to see more activities.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Announcements Component
  const Announcements = () => {
    const relevantAnnouncements = announcements
      .filter(announcement => announcement.targetRoles.includes(currentUser.role))
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    const markAsRead = async (announcementId) => {
      const announcementRef = doc(db, 'announcements', announcementId);
      try {
        const announcement = announcements.find(ann => ann.id === announcementId);
        if (announcement && !announcement.readBy.includes(currentUser.id)) {
          await updateDoc(announcementRef, {
            readBy: [...announcement.readBy, currentUser.id]
          });
        }
      } catch (error) {
        console.error("Error marking announcement as read: ", error);
      }
    };

    const openAnnouncementModal = () => {
      setSelectedEmployee({ title: '', content: '', targetRoles: [], priority: 'medium' });
      setModalType('announcement');
      setIsModalOpen(true);
    };

    const deleteAnnouncement = async (announcementId) => {
      const announcementRef = doc(db, 'announcements', announcementId);
      try {
        const announcement = announcements.find(ann => ann.id === announcementId);
        await deleteDoc(announcementRef);
        logActivity(currentUser.id, 'announcement_deleted',
          `Deleted announcement: "${announcement?.title}"`, { announcementId });
      } catch (error) {
        console.error("Error deleting announcement: ", error);
      }
    };

    const canSend = currentUser.role === 'admin' || currentUser.role === 'manager';

    const formatTime = (timestamp) => {
      const date = new Date(timestamp);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'high': return 'border-l-red-500 bg-red-50';
        case 'medium': return 'border-l-yellow-500 bg-yellow-50';
        case 'low': return 'border-l-green-500 bg-green-50';
        default: return 'border-l-gray-500 bg-gray-50';
      }
    };
    
    const getPriorityBadge = (priority) => {
      const colors = {
        high: 'bg-red-100 text-red-800',
        medium: 'bg-yellow-100 text-yellow-800',
        low: 'bg-green-100 text-green-800'
      };
      return colors[priority] || 'bg-gray-100 text-gray-800';
    };

    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Company Announcements</h2>
          {canSend && (
            <button 
              onClick={openAnnouncementModal}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
            >
              <Send className="w-4 h-4 inline mr-1" /> Send Announcement
            </button>
          )}
        </div>

        <div className="space-y-4">
          {relevantAnnouncements.map(announcement => {
            const author = employees.find(emp => emp.id === announcement.authorId);
            const isUnread = !announcement.readBy.includes(currentUser.id);
            const isAuthor = announcement.authorId === currentUser.id;

            return (
              <div 
                key={announcement.id} 
                className={`bg-white rounded-lg shadow border-l-4 ${getPriorityColor(announcement.priority)} ${isUnread ? 'ring-2 ring-blue-200' : ''}`}
                onClick={() => isUnread && markAsRead(announcement.id)}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className={`text-lg font-semibold ${isUnread ? 'text-blue-900' : 'text-gray-900'}`}>{announcement.title}</h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(announcement.priority)}`}>{announcement.priority}</span>
                        {isUnread && <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">New</span>}
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{announcement.content}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span>From: {author?.name} ({author?.role})</span>
                          <span>To: {announcement.targetRoles.join(', ')}</span>
                          <span>{formatTime(announcement.timestamp)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span>{announcement.readBy.length} read</span>
                          {isAuthor && (
                            <button
                              onClick={(e) => { e.stopPropagation(); deleteAnnouncement(announcement.id); }}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {relevantAnnouncements.length === 0 && (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <Megaphone className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Announcements</h3>
              <p className="text-gray-500">{canSend ? "You haven't sent any announcements yet." : "No announcements for you at this time."}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Shift Swaps Component (Employee view)
  const ShiftSwaps = () => {
    const myShifts = shifts.filter(shift => 
      shift.employeeId === currentUser.id && new Date(shift.date) >= new Date()
    );
    const mySwapRequests = shiftSwapRequests.filter(req => req.fromEmployeeId === currentUser.id);
    const incomingSwapRequests = shiftSwapRequests.filter(req => req.toEmployeeId === currentUser.id);

    const requestSwap = (shiftId) => {
      setSelectedEmployee({ fromEmployeeId: currentUser.id, shiftId, toEmployeeId: '', reason: '' });
      setModalType('shiftSwap');
      setIsModalOpen(true);
    };

    const respondToSwap = async (requestId, response) => {
      const requestRef = doc(db, 'shiftSwapRequests', requestId);
      const swapRequest = shiftSwapRequests.find(req => req.id === requestId);
      if (!swapRequest) return;

      try {
        if (response === 'approved') {
          await updateDoc(requestRef, { status: 'agreed' });
          const managers = employees.filter(emp => emp.role === 'admin' || emp.role === 'manager');
          const fromEmployee = employees.find(emp => emp.id === swapRequest.fromEmployeeId);
          const shift = shifts.find(s => s.id === swapRequest.shiftId);

          managers.forEach(manager => {
            createNotification(manager.id, 'swap_approval_needed',
              `${fromEmployee.name} and ${currentUser.name} agreed to swap shifts on ${new Date(shift.date).toLocaleDateString()}`, requestId);
          });

          createNotification(swapRequest.fromEmployeeId, 'swap_agreed',
            `${currentUser.name} agreed to your shift swap request for ${new Date(shift.date).toLocaleDateString()}`, requestId);
        } else {
          await updateDoc(requestRef, { status: 'denied' });
          const shift = shifts.find(s => s.id === swapRequest.shiftId);
          createNotification(swapRequest.fromEmployeeId, 'swap_denied',
            `${currentUser.name} declined your shift swap request for ${new Date(shift.date).toLocaleDateString()}`, requestId);
        }
      } catch (error) {
        console.error("Error responding to swap request: ", error);
      }
    };

    const getStatusDisplay = (status) => {
      const config = {
        pending: { text: 'Waiting for response', color: 'yellow' },
        agreed: { text: 'Agreed - Pending management approval', color: 'blue' },
        approved: { text: 'Approved by management', color: 'green' },
        denied: { text: 'Denied', color: 'red' }
      }[status] || { text: status, color: 'gray' };
      return <span className={`px-2 py-1 text-xs rounded-full bg-${config.color}-100 text-${config.color}-800`}>{config.text}</span>;
    };

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Shift Swaps</h2>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">My Upcoming Shifts</h3>
          <div className="space-y-2">
            {myShifts.map(shift => (
              <div key={shift.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <p className="font-medium">{new Date(shift.date).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600">{shift.position} • {shift.startTime} - {shift.endTime}</p>
                </div>
                <button
                  onClick={() => requestSwap(shift.id)}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >Request Swap</button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">My Swap Requests</h3>
          <div className="space-y-2">
            {mySwapRequests.map(request => {
              const shift = shifts.find(s => s.id === request.shiftId);
              const toEmployee = employees.find(emp => emp.id === request.toEmployeeId);
              return (
                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">Swap request to {toEmployee?.name}</p>
                    <p className="text-sm text-gray-600">{new Date(shift.date).toLocaleDateString()} • {shift.startTime} - {shift.endTime}</p>
                    <p className="text-sm text-gray-500">Reason: {request.reason}</p>
                  </div>
                  {getStatusDisplay(request.status)}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Incoming Swap Requests</h3>
          <div className="space-y-2">
            {incomingSwapRequests.map(request => {
              const shift = shifts.find(s => s.id === request.shiftId);
              const fromEmployee = employees.find(emp => emp.id === request.fromEmployeeId);
              return (
                <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <p className="font-medium">{fromEmployee?.name} wants to swap</p>
                    <p className="text-sm text-gray-600">{new Date(shift.date).toLocaleDateString()} • {shift.startTime} - {shift.endTime}</p>
                    <p className="text-sm text-gray-500">Reason: {request.reason}</p>
                  </div>
                  {request.status === 'pending' ? (
                    <div className="flex space-x-2">
                      <button onClick={() => respondToSwap(request.id, 'approved')} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">Accept</button>
                      <button onClick={() => respondToSwap(request.id, 'denied')} className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">Decline</button>
                    </div>
                  ) : getStatusDisplay(request.status)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Approvals Component (Management only)
  const Approvals = () => {
    const agreedSwaps = shiftSwapRequests.filter(req => req.status === 'agreed');
    const pendingTimeOff = timeOffRequests.filter(req => req.status === 'pending');

    const approveSwap = async (requestId) => {
      const requestRef = doc(db, 'shiftSwapRequests', requestId);
      const swapRequest = shiftSwapRequests.find(req => req.id === requestId);
      if (!swapRequest) return;

      const shiftRef = doc(db, 'shifts', swapRequest.shiftId);

      try {
        const batch = writeBatch(db);
        batch.update(requestRef, { status: 'approved' });
        batch.update(shiftRef, { employeeId: swapRequest.toEmployeeId });
        await batch.commit();

        const shift = shifts.find(s => s.id === swapRequest.shiftId);
        notifyUsers([swapRequest.fromEmployeeId, swapRequest.toEmployeeId], 'swap_approved', `Shift swap approved for ${new Date(shift.date).toLocaleDateString()}`, requestId);

        const fromEmployee = employees.find(emp => emp.id === swapRequest.fromEmployeeId);
        const toEmployee = employees.find(emp => emp.id === swapRequest.toEmployeeId);
        logActivity(currentUser.id, 'shift_swap_approved', `Approved shift swap between ${fromEmployee?.name} and ${toEmployee?.name} for ${new Date(shift.date).toLocaleDateString()}`, { swapId: requestId, shiftId: swapRequest.shiftId });
      } catch (error) {
        console.error("Error approving swap: ", error);
      }
    };

    const denySwap = async (requestId) => {
      const requestRef = doc(db, 'shiftSwapRequests', requestId);
      try {
        await updateDoc(requestRef, { status: 'denied' });

        const swapRequest = shiftSwapRequests.find(req => req.id === requestId);
        const shift = shifts.find(s => s.id === swapRequest.shiftId);
        notifyUsers([swapRequest.fromEmployeeId, swapRequest.toEmployeeId], 'swap_denied', `Shift swap denied for ${new Date(shift.date).toLocaleDateString()}`, requestId);
        const fromEmployee = employees.find(emp => emp.id === swapRequest.fromEmployeeId);
        const toEmployee = employees.find(emp => emp.id === swapRequest.toEmployeeId);
        logActivity(currentUser.id, 'shift_swap_denied', `Denied shift swap between ${fromEmployee?.name} and ${toEmployee?.name} for ${new Date(shift.date).toLocaleDateString()}`, { swapId: requestId, shiftId: swapRequest.shiftId });
      } catch (error) {
        console.error("Error denying swap: ", error);
      }
    };

    const approveTimeOff = async (requestId) => {
      const requestRef = doc(db, 'timeOffRequests', requestId);
      try {
        await updateDoc(requestRef, { status: 'approved' });
        const request = timeOffRequests.find(req => req.id === requestId);
        createNotification(request.employeeId, 'time_off_approved', `Time off approved for ${request.startDate} to ${request.endDate}`, requestId);
      } catch (error) {
        console.error("Error approving time off: ", error);
      }
    };

    const denyTimeOff = async (requestId) => {
      const requestRef = doc(db, 'timeOffRequests', requestId);
      try {
        await updateDoc(requestRef, { status: 'denied' });
        const request = timeOffRequests.find(req => req.id === requestId);
        createNotification(request.employeeId, 'time_off_denied', `Time off denied for ${request.startDate} to ${request.endDate}`, requestId);
      } catch (error) {
        console.error("Error denying time off: ", error);
      }
    };

    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6">Pending Approvals</h2>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Shift Swap Approvals</h3>
          <div className="space-y-4">
            {agreedSwaps.map(request => {
              const shift = shifts.find(s => s.id === request.shiftId);
              const fromEmployee = employees.find(emp => emp.id === request.fromEmployeeId);
              const toEmployee = employees.find(emp => emp.id === request.toEmployeeId);
              return (
                <div key={request.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{fromEmployee?.name} ↔ {toEmployee?.name}</h4>
                      <p className="text-sm text-gray-600">{new Date(shift?.date).toLocaleDateString()} • {shift?.startTime} - {shift?.endTime}</p>
                      <p className="text-sm text-gray-600">Position: {shift?.position}</p>
                      <p className="text-sm text-gray-500 mt-1">Reason: {request.reason}</p>
                      <p className="text-xs text-gray-400">Both parties agreed on {new Date(request.requestDate).toLocaleDateString()}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => approveSwap(request.id)} className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700">Approve Swap</button>
                      <button onClick={() => denySwap(request.id)} className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700">Deny Swap</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {agreedSwaps.length === 0 && <p className="text-gray-500">No swap requests pending approval</p>}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Time Off Requests</h3>
          <div className="space-y-4">
            {pendingTimeOff.map(request => {
              const employee = employees.find(emp => emp.id === request.employeeId);
              return (
                <div key={request.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{employee?.name}</h4>
                      <p className="text-sm text-gray-600">{request.startDate} to {request.endDate}</p>
                      <p className="text-sm text-gray-500">Reason: {request.reason}</p>
                      <p className="text-xs text-gray-400">Requested on {new Date(request.requestDate).toLocaleDateString()}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => approveTimeOff(request.id)} className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700">Approve</button>
                      <button onClick={() => denyTimeOff(request.id)} className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700">Deny</button>
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingTimeOff.length === 0 && <p className="text-gray-500">No time off requests pending approval</p>}
          </div>
        </div>
      </div>
    );
  };

  // Modal Component
  const Modal = () => {
    if (!isModalOpen) return null;

    const handleSave = async () => {
      try {
        if (modalType === 'employee') {
          if (selectedEmployee.id) {
            const employeeRef = doc(db, 'employees', selectedEmployee.id);
            await updateDoc(employeeRef, selectedEmployee);
            logActivity(currentUser.id, 'employee_updated', `Updated ${selectedEmployee.name}`);
          } else {
            // This case should be handled by a separate user creation flow with Firebase Auth
            console.warn("Employee creation without auth is not supported in this refactor.");
          }
        } else if (modalType === 'shift') {
          if (selectedEmployee.id) {
            const shiftRef = doc(db, 'shifts', selectedEmployee.id);
            await updateDoc(shiftRef, selectedEmployee);
            createNotification(selectedEmployee.employeeId, 'schedule_change', `Your shift on ${selectedEmployee.date} has been updated`, selectedEmployee.id);
            const employee = employees.find(emp => emp.id === selectedEmployee.employeeId);
            logActivity(currentUser.id, 'shift_updated', `Updated shift for ${employee?.name} on ${selectedEmployee.date}`);
          } else {
            const docRef = await addDoc(collection(db, 'shifts'), selectedEmployee);
            await updateDoc(docRef, { id: docRef.id });
            createNotification(selectedEmployee.employeeId, 'new_shift', `New shift assigned for ${selectedEmployee.date}`, docRef.id);
            const employee = employees.find(emp => emp.id === selectedEmployee.employeeId);
            logActivity(currentUser.id, 'shift_created', `Created shift for ${employee?.name} on ${selectedEmployee.date}`);
          }
        } else if (modalType === 'timeOffRequest') {
            const docRef = await addDoc(collection(db, 'timeOffRequests'), { ...selectedEmployee, status: 'pending', requestDate: new Date().toISOString() });
            await updateDoc(docRef, { id: docRef.id });
            const managers = employees.filter(emp => emp.role === 'admin' || emp.role === 'manager').map(m => m.id);
            notifyUsers(managers, 'time_off_request', `${currentUser.name} requested time off`);
            logActivity(currentUser.id, 'time_off_requested', `Requested time off from ${selectedEmployee.startDate} to ${selectedEmployee.endDate}`);
        } else if (modalType === 'shiftSwap') {
            const docRef = await addDoc(collection(db, 'shiftSwapRequests'), { ...selectedEmployee, status: 'pending', requestDate: new Date().toISOString() });
            await updateDoc(docRef, { id: docRef.id });
            const shift = shifts.find(s => s.id === selectedEmployee.shiftId);
            createNotification(selectedEmployee.toEmployeeId, 'swap_request', `${currentUser.name} wants to swap shifts with you on ${new Date(shift.date).toLocaleDateString()}`, docRef.id);
            const toEmployee = employees.find(emp => emp.id === selectedEmployee.toEmployeeId);
            logActivity(currentUser.id, 'shift_swap_requested', `Requested shift swap with ${toEmployee?.name}`);
        } else if (modalType === 'announcement') {
            const docRef = await addDoc(collection(db, 'announcements'), { ...selectedEmployee, authorId: currentUser.id, timestamp: new Date().toISOString(), readBy: [] });
            await updateDoc(docRef, { id: docRef.id });
            const targetEmployees = employees.filter(emp => selectedEmployee.targetRoles.includes(emp.role) && emp.id !== currentUser.id).map(e => e.id);
            notifyUsers(targetEmployees, 'announcement', `New announcement: ${selectedEmployee.title}`, docRef.id);
            logActivity(currentUser.id, 'announcement_sent', `Sent announcement "${selectedEmployee.title}"`);
        }
      } catch (error) {
        console.error("Error saving data: ", error);
        alert("There was an error saving your changes. Please try again.");
      }
      setIsModalOpen(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false); }}>
        <div className="bg-white p-6 rounded-lg w-full max-w-md">
          <h3 className="text-lg font-semibold mb-4">
            {modalType === 'employee' && 'Employee Details'}
            {modalType === 'shift' && 'Shift Details'}
            {modalType === 'timeOffRequest' && 'Time Off Request'}
            {modalType === 'shiftSwap' && 'Shift Swap Request'}
            {modalType === 'announcement' && 'Send Announcement'}
          </h3>
          {modalType === 'employee' ? (
            <div className="space-y-4">
              <input type="text" placeholder="Name" value={selectedEmployee?.name || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, name: e.target.value }))} className="w-full p-2 border rounded" />
              <select value={selectedEmployee?.position || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, position: e.target.value }))} className="w-full p-2 border rounded">
                <option value="">Select Position</option>
                {positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
              </select>
              <select value={selectedEmployee?.role || 'employee'} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, role: e.target.value }))} className="w-full p-2 border rounded">
                <option value="employee">Employee</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </select>
              <input type="email" placeholder="Email" value={selectedEmployee?.email || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, email: e.target.value }))} className="w-full p-2 border rounded" />
              <input type="tel" placeholder="Phone" value={selectedEmployee?.phone || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, phone: e.target.value }))} className="w-full p-2 border rounded" />
              <input type="number" placeholder="Hourly Rate" step="0.01" value={selectedEmployee?.hourlyRate || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, hourlyRate: parseFloat(e.target.value) }))} className="w-full p-2 border rounded" />
            </div>
          ) : modalType === 'shift' ? (
            <div className="space-y-4">
              <select value={selectedEmployee?.employeeId || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, employeeId: parseInt(e.target.value) }))} className="w-full p-2 border rounded">
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
              <input type="date" value={selectedEmployee?.date || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, date: e.target.value }))} className="w-full p-2 border rounded" />
              <TimePicker placeholder="Start Time" value={selectedEmployee?.startTime || ''} onChange={(time) => setSelectedEmployee(prev => ({ ...prev, startTime: time }))} />
              <TimePicker placeholder="End Time" value={selectedEmployee?.endTime || ''} onChange={(time) => setSelectedEmployee(prev => ({ ...prev, endTime: time }))} />
              <select value={selectedEmployee?.position || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, position: e.target.value }))} className="w-full p-2 border rounded">
                <option value="">Select Position</option>
                {positions.map(pos => <option key={pos} value={pos}>{pos}</option>)}
              </select>
            </div>
          ) : modalType === 'timeOffRequest' ? (
            <div className="space-y-4">
              <input type="date" placeholder="Start Date" value={selectedEmployee?.startDate || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, startDate: e.target.value }))} className="w-full p-2 border rounded" />
              <input type="date" placeholder="End Date" value={selectedEmployee?.endDate || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, endDate: e.target.value }))} className="w-full p-2 border rounded" />
              <textarea placeholder="Reason for time off" value={selectedEmployee?.reason || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, reason: e.target.value }))} className="w-full p-2 border rounded h-24 resize-none" />
            </div>
          ) : modalType === 'shiftSwap' ? (
            <div className="space-y-4">
              <select value={selectedEmployee?.toEmployeeId || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, toEmployeeId: parseInt(e.target.value) }))} className="w-full p-2 border rounded">
                <option value="">Select Employee to Swap With</option>
                {employees.filter(emp => emp.id !== currentUser.id).map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
              <textarea placeholder="Reason for swap request" value={selectedEmployee?.reason || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, reason: e.target.value }))} className="w-full p-2 border rounded h-24 resize-none" />
            </div>
          ) : modalType === 'announcement' ? (
            <div className="space-y-4">
              <input type="text" placeholder="Announcement Title" value={selectedEmployee?.title || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, title: e.target.value }))} className="w-full p-2 border rounded" />
              <textarea placeholder="Announcement content" value={selectedEmployee?.content || ''} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, content: e.target.value }))} className="w-full p-2 border rounded h-32 resize-none" />
              <select value={selectedEmployee?.priority || 'medium'} onChange={(e) => setSelectedEmployee(prev => ({ ...prev, priority: e.target.value }))} className="w-full p-2 border rounded">
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
              <div>
                <label className="block text-sm font-medium mb-2">Send To:</label>
                <div className="space-y-2">
                  {currentUser.role === 'admin' && (
                    <label className="flex items-center">
                      <input type="checkbox" checked={selectedEmployee?.targetRoles?.includes('manager') || false} onChange={(e) => { const roles = selectedEmployee?.targetRoles || []; if (e.target.checked) { setSelectedEmployee(prev => ({ ...prev, targetRoles: [...roles.filter(r => r !== 'manager'), 'manager'] })); } else { setSelectedEmployee(prev => ({ ...prev, targetRoles: roles.filter(r => r !== 'manager') })); } }} className="mr-2" />
                      Managers
                    </label>
                  )}
                  <label className="flex items-center">
                    <input type="checkbox" checked={selectedEmployee?.targetRoles?.includes('employee') || false} onChange={(e) => { const roles = selectedEmployee?.targetRoles || []; if (e.target.checked) { setSelectedEmployee(prev => ({ ...prev, targetRoles: [...roles.filter(r => r !== 'employee'), 'employee'] })); } else { setSelectedEmployee(prev => ({ ...prev, targetRoles: roles.filter(r => r !== 'employee') })); } }} className="mr-2" />
                    Employees
                  </label>
                </div>
              </div>
            </div>
          ) : null}
          <div className="flex justify-end space-x-2 mt-6">
            <button onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 border rounded hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">
              {modalType === 'timeOffRequest' ? 'Submit Request' : modalType === 'shiftSwap' ? 'Send Request' : modalType === 'announcement' ? 'Send Announcement' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main render logic
  if (!currentUser) {
    return <LoginForm />;
  }

  return (
    <div className="min-h-screen bg-gray-100" onClick={(e) => { if (showNotifications && !e.target.closest('.notifications-dropdown')) { setShowNotifications(false); } }}>
      <Navigation />
      
      {activeView === 'dashboard' && (currentUser.role === 'employee' ? <EmployeeDashboard /> : <Dashboard />)}
      {activeView === 'schedule' && <Schedule />}
      {(currentUser.role === 'admin' || currentUser.role === 'manager') && activeView === 'employees' && <Employees />}
      {activeView === 'timeoff' && <TimeOff />}
      {currentUser.role === 'employee' && activeView === 'swaps' && <ShiftSwaps />}
      {activeView === 'announcements' && <Announcements />}
      {(currentUser.role === 'admin' || currentUser.role === 'manager') && activeView === 'approvals' && <Approvals />}
      {currentUser.role === 'admin' && activeView === 'activity' && <ActivityLog />}
      
      <Modal />
    </div>
  );
}

// This is the final App wrapper component. It includes the SchedulingApp
// and adds a connection status indicator for a better user experience.
function App() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const ConnectionStatus = () => {
    if (isOnline) return null;
    
    return (
      <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center py-2 z-50">
        <p className="text-sm">You are currently offline. Some features may not work properly.</p>
      </div>
    );
  };

  return (
    <>
      <ConnectionStatus />
      <div className={isOnline ? '' : 'pt-12'}>
        <SchedulingApp />
      </div>
    </>
  );
}

export default App;
